import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

const MemoryMode = () => {
  const [selectedTable, setSelectedTable] = useState(2);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [totalQuestions] = useState(12);

  const currentQuestion = {
    num1: selectedTable,
    num2: currentIndex + 1,
    answer: selectedTable * (currentIndex + 1)
  };

  const checkAnswer = () => {
    const userNum = parseInt(userAnswer);
    const correct = userNum === currentQuestion.answer;
    setIsCorrect(correct);
    setShowAnswer(true);
    
    if (correct) {
      setScore(score + 1);
      setStreak(streak + 1);
    } else {
      setStreak(0);
    }
  };

  const nextQuestion = () => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
    setUserAnswer("");
    setShowAnswer(false);
    setIsCorrect(null);
  };

  const resetMode = () => {
    setCurrentIndex(0);
    setUserAnswer("");
    setScore(0);
    setStreak(0);
    setShowAnswer(false);
    setIsCorrect(null);
  };

  const progress = ((currentIndex + 1) / totalQuestions) * 100;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Mode Hafalan
            <Badge variant={streak >= 5 ? "default" : "secondary"}>
              Streak: {streak} 🔥
            </Badge>
          </CardTitle>
          <CardDescription>
            Latih hafalan tabel perkalian dengan skor dan streak
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium">Tabel:</label>
            <select
              value={selectedTable}
              onChange={(e) => {
                setSelectedTable(parseInt(e.target.value));
                resetMode();
              }}
              className="px-3 py-2 border border-input rounded-md bg-background"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
            <Button onClick={resetMode} variant="outline" size="sm">
              Reset
            </Button>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress: {currentIndex + 1}/{totalQuestions}</span>
              <span>Skor: {score}/{totalQuestions}</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          <Card className="bg-gradient-to-r from-primary/5 to-secondary/5">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="text-4xl font-bold">
                  {currentQuestion.num1} × {currentQuestion.num2} = ?
                </div>
                
                {!showAnswer ? (
                  <div className="space-y-4">
                    <Input
                      type="number"
                      value={userAnswer}
                      onChange={(e) => setUserAnswer(e.target.value)}
                      placeholder="Masukkan jawaban"
                      className="text-center text-xl"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && userAnswer) {
                          checkAnswer();
                        }
                      }}
                    />
                    <Button 
                      onClick={checkAnswer} 
                      disabled={!userAnswer}
                      className="w-full"
                    >
                      Cek Jawaban
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className={`text-2xl font-bold ${
                      isCorrect ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {isCorrect ? '✅ Benar!' : '❌ Salah!'}
                    </div>
                    <div className="text-xl">
                      Jawaban yang benar: <span className="font-bold">{currentQuestion.answer}</span>
                    </div>
                    <Button onClick={nextQuestion} className="w-full">
                      {currentIndex < totalQuestions - 1 ? 'Soal Berikutnya' : 'Kembali ke Awal'}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4 text-center">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-primary">{Math.round((score / Math.max(currentIndex + (showAnswer ? 1 : 0), 1)) * 100)}%</div>
                <div className="text-sm text-muted-foreground">Akurasi</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-secondary">{streak}</div>
                <div className="text-sm text-muted-foreground">Streak Terbaik</div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MemoryMode;